import { useCallback, useEffect, useRef, useState } from "react";

import { defaultIsOnline } from "../capture/use-offline-queue";
import { fetchMyShifts, type MyShiftRow, type MyShiftsQueryClient } from "./my-shifts";
import {
  DECLINE_REASONS,
  submitScheduleResponse,
  type DeclineReason,
  type ScheduleResponseRpcClient,
} from "./submit-response";

// My Shifts (ship-dark behind web_my_shifts). The guard's three questions, in order: when do I
// work next, did I tell them I'm coming, can I get out of Tuesday. Soft-decline per the approved
// standalone: declining never removes the card - it grays, stays interactive, and offers the
// one-tap take-back while the assignment is still open. Online-only BY DESIGN, same honesty as
// the timeclock panel. Times render in the device's local zone (guards live where they work).

type PanelClient = MyShiftsQueryClient & ScheduleResponseRpcClient;

function formatWindow(startsAt: string | null, endsAt: string | null): string {
  if (startsAt === null || endsAt === null) {
    return "Time not set";
  }
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const time = (date: Date) => date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  const crossesDay = start.toDateString() !== end.toDateString();
  return `${day} - ${time(start)} to ${time(end)}${crossesDay ? " +1" : ""}`;
}

function reasonLabel(value: string | null): string | null {
  const match = DECLINE_REASONS.find((reason) => reason.value === value);
  return match ? match.label : null;
}

function responseCopy(shift: MyShiftRow): string {
  if (shift.responseStatus === "acknowledged") {
    return "Confirmed";
  }
  if (shift.responseStatus === "declined") {
    const label = reasonLabel(shift.declineReason);
    return label ? `Declined - ${label}` : "Declined";
  }
  return "Awaiting confirm";
}

function humanizeCredential(value: string): string {
  const words = value.replaceAll("_", " ").trim();
  return words.length === 0 ? value : words[0]!.toUpperCase() + words.slice(1);
}

function formatClock(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

export function MyShiftsPanel({
  client,
  accountId,
  isOnline = defaultIsOnline,
  now = () => new Date(),
}: {
  client: PanelClient;
  accountId: string;
  /** Test seam; defaults to navigator.onLine. */
  isOnline?: () => boolean;
  /** Test seam for the upcoming-window edge. */
  now?: () => Date;
}) {
  // Pinned per mount (useState initializer, not useMemo on `now`): a fresh default-prop
  // identity each render must not retrigger the fetch and stomp local response state.
  const [fromIso] = useState(() => now().toISOString());
  const [shifts, setShifts] = useState<MyShiftRow[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [declineFor, setDeclineFor] = useState<string | null>(null);
  const [chosenReason, setChosenReason] = useState<DeclineReason | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // GW-SHIFT-CONTEXT-POLISH: capture stays dominant - the full card list is collapsed behind
  // the digest by default; refresh is MANUAL and single-flight (no polling, no click storms).
  const [expanded, setExpanded] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const nowRef = useRef(now);
  nowRef.current = now;
  const online = isOnline();

  const load = useCallback(
    async (windowFromIso: string) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      try {
        const rows = await fetchMyShifts(client, accountId, windowFromIso);
        if (!mounted.current) {
          return;
        }
        setShifts(rows);
        setLoadFailed(false);
        setRefreshedAt(nowRef.current());
      } catch {
        if (!mounted.current) {
          return;
        }
        setLoadFailed(true);
      } finally {
        inFlight.current = false;
      }
    },
    [client, accountId],
  );

  useEffect(() => {
    void load(fromIso);
  }, [load, fromIso]);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  async function respond(shift: MyShiftRow, status: "acknowledged" | "declined", reason?: DeclineReason) {
    setPending(shift.routeAssignmentId);
    setActionError(null);
    const result = await submitScheduleResponse(client, {
      accountId,
      routeAssignmentId: shift.routeAssignmentId,
      responseStatus: status,
      ...(reason !== undefined ? { declineReason: reason } : {}),
    });
    setPending(null);
    if (!result.ok) {
      setActionError(result.message);
      return;
    }
    setDeclineFor(null);
    setChosenReason(null);
    setShifts((current) =>
      (current ?? []).map((row) =>
        row.routeAssignmentId === shift.routeAssignmentId
          ? {
              ...row,
              responseStatus: status,
              declineReason: status === "declined" ? (reason ?? null) : null,
              respondedAt: nowRef.current().toISOString(),
            }
          : row,
      ),
    );
  }

  if (loadFailed) {
    return (
      <section className="myshifts" aria-label="My shifts">
        <h2 className="myshifts__title">My shifts</h2>
        <p className="myshifts__state" role="alert">Could not load your shifts. Pull to retry later.</p>
      </section>
    );
  }
  if (shifts === null) {
    return (
      <section className="myshifts" aria-label="My shifts">
        <h2 className="myshifts__title">My shifts</h2>
        <p className="myshifts__state" role="status">Loading your shifts...</p>
      </section>
    );
  }

  return (
    <section className="myshifts" aria-label="My shifts">
      <h2 className="myshifts__title">My shifts</h2>
      {!online ? (
        <p className="myshifts__offline" role="status">
          You are offline - your shifts are shown, but confirming or declining needs you back online.
        </p>
      ) : null}
      {actionError ? (
        <p className="myshifts__state myshifts__state--error" role="alert">{actionError}</p>
      ) : null}
      {shifts.length === 0 ? (
        <p className="myshifts__state" role="status">
          No upcoming shifts. Your supervisor publishes schedules here.
        </p>
      ) : (
        <>
          <div className="myshifts__digest" aria-label="Next shift">
            <p className="myshifts__digest-eyebrow">Next shift</p>
            <p className="myshifts__where">
              {shifts[0]!.siteName ?? "Site"}
              {shifts[0]!.routeName ? ` - ${shifts[0]!.routeName}` : ""}
            </p>
            <p className="myshifts__when">{formatWindow(shifts[0]!.startsAt, shifts[0]!.endsAt)}</p>
            <p className="myshifts__digest-state">
              <span className={`myshifts__digest-response myshifts__digest-response--${shifts[0]!.responseStatus}`}>
                {responseCopy(shifts[0]!)}
              </span>
              {shifts[0]!.requiredCredentialTypes.map((type) => (
                <span key={type} className="myshifts__credential">
                  {humanizeCredential(type)}
                </span>
              ))}
            </p>
          </div>
          <div className="myshifts__meta">
            {refreshedAt ? (
              <span className="myshifts__updated">Updated {formatClock(refreshedAt)}</span>
            ) : null}
            <button
              type="button"
              className="myshifts__refresh"
              onClick={() => void load(now().toISOString())}
            >
              Refresh
            </button>
            <button type="button" className="myshifts__toggle" onClick={() => setExpanded((open) => !open)}>
              {expanded ? "Hide shifts" : `Show all ${shifts.length} shift${shifts.length === 1 ? "" : "s"}`}
            </button>
          </div>
          {expanded
            ? shifts.map((shift) => {
          const declined = shift.responseStatus === "declined";
          const confirmed = shift.responseStatus === "acknowledged";
          const busy = pending === shift.routeAssignmentId;
          return (
            <article
              key={shift.routeAssignmentId}
              className={`myshifts__card${declined ? " myshifts__card--declined" : ""}`}
            >
              <p className="myshifts__where">
                {shift.siteName ?? "Site"}
                {shift.routeName ? ` - ${shift.routeName}` : ""}
              </p>
              <p className="myshifts__when">{formatWindow(shift.startsAt, shift.endsAt)}</p>
              {confirmed ? (
                <p className="myshifts__confirmed">Confirmed</p>
              ) : declined ? (
                <div className="myshifts__declined-row">
                  <p className="myshifts__declined-note">
                    Declined{reasonLabel(shift.declineReason) ? ` - ${reasonLabel(shift.declineReason)}` : ""}
                  </p>
                  <button
                    type="button"
                    className="myshifts__takeback"
                    disabled={!online || busy}
                    onClick={() => void respond(shift, "acknowledged")}
                  >
                    Take this shift
                  </button>
                </div>
              ) : declineFor === shift.routeAssignmentId ? (
                <fieldset className="myshifts__sheet" role="group" aria-label="Why can't you make it?">
                  <legend className="myshifts__sheet-title">Why can't you make it?</legend>
                  {DECLINE_REASONS.map((reason) => (
                    <label key={reason.value} className="myshifts__reason">
                      <input
                        type="radio"
                        name={`reason-${shift.routeAssignmentId}`}
                        value={reason.value}
                        checked={chosenReason === reason.value}
                        onChange={() => setChosenReason(reason.value)}
                      />
                      <span>{reason.label}</span>
                    </label>
                  ))}
                  <div className="myshifts__sheet-actions">
                    <button
                      type="button"
                      className="myshifts__decline"
                      disabled={!online || busy || chosenReason === null}
                      onClick={() => {
                        if (chosenReason !== null) {
                          void respond(shift, "declined", chosenReason);
                        }
                      }}
                    >
                      Decline shift
                    </button>
                    <button
                      type="button"
                      className="myshifts__cancel"
                      onClick={() => {
                        setDeclineFor(null);
                        setChosenReason(null);
                      }}
                    >
                      Keep it
                    </button>
                  </div>
                </fieldset>
              ) : (
                <div className="myshifts__actions">
                  <button
                    type="button"
                    className="myshifts__confirm"
                    disabled={!online || busy}
                    onClick={() => void respond(shift, "acknowledged")}
                  >
                    Confirm shift
                  </button>
                  <button
                    type="button"
                    className="myshifts__cantmake"
                    disabled={!online || busy}
                    onClick={() => {
                      setDeclineFor(shift.routeAssignmentId);
                      setChosenReason(null);
                    }}
                  >
                    Can't make it
                  </button>
                </div>
              )}
            </article>
          );
            })
            : null}
        </>
      )}
    </section>
  );
}
